import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  input,
  OnInit,
  output,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterLink } from '@angular/router';
import { filter } from 'rxjs';
import { StyleClass } from 'primeng/styleclass';
import { Toast } from 'primeng/toast';
import { Tooltip } from 'primeng/tooltip';
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { navItemMatchesUrl, ShellNavItem } from './shell-nav-item';

@Component({
  selector: 'shared-app-shell',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [RouterLink, StyleClass, Toast, Tooltip, DialogModule, ButtonModule],
  templateUrl: './app-shell.component.html',
  styleUrl: './app-shell.component.scss',
})
export class AppShellComponent implements OnInit {
  private router = inject(Router);
  private destroyRef = inject(DestroyRef);

  navItems = input<ShellNavItem[]>([]);
  showShell = input(true);
  userEmail = input<string | null>(null);

  login = output<void>();
  logout = output<void>();

  activeItem = signal(0);
  profileDialogVisible = signal(false);
  sidebarCompressed = signal(false);
  isDesktopViewport = signal(false);
  canHover = signal(false);

  private desktopMediaQuery = window.matchMedia('(min-width: 1024px)');
  private hoverMediaQuery = window.matchMedia('(hover: hover)');

  ngOnInit(): void {
    this.initSidebarCompression();
    this.syncActiveItem(this.router.url);

    this.router.events
      .pipe(
        filter(
          (event): event is NavigationEnd => event instanceof NavigationEnd,
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((event) => {
        this.syncActiveItem(event.urlAfterRedirects);
      });
  }

  private initSidebarCompression(): void {
    this.isDesktopViewport.set(this.desktopMediaQuery.matches);
    this.canHover.set(this.hoverMediaQuery.matches);

    const onViewportChange = (event: MediaQueryListEvent) => {
      this.isDesktopViewport.set(event.matches);
      if (!event.matches) {
        this.sidebarCompressed.set(false);
      }
    };
    const onHoverCapabilityChange = (event: MediaQueryListEvent) => {
      this.canHover.set(event.matches);
    };

    this.desktopMediaQuery.addEventListener('change', onViewportChange);
    this.hoverMediaQuery.addEventListener('change', onHoverCapabilityChange);
    this.destroyRef.onDestroy(() => {
      this.desktopMediaQuery.removeEventListener('change', onViewportChange);
      this.hoverMediaQuery.removeEventListener(
        'change',
        onHoverCapabilityChange,
      );
    });
  }

  private syncActiveItem(url: string): void {
    const matchingIndex = this.navItems().findIndex((item) =>
      navItemMatchesUrl(item, url),
    );
    this.activeItem.set(matchingIndex !== -1 ? matchingIndex : 0);
  }

  setActiveItem(index: number): void {
    this.activeItem.set(index);
  }

  compressSidebar(): void {
    if (this.isDesktopViewport()) {
      this.sidebarCompressed.set(true);
    }
  }

  expandSidebar(): void {
    this.sidebarCompressed.set(false);
  }

  onSidebarLogoClick(event: Event): void {
    if (!this.canHover() && this.sidebarCompressed()) {
      if (event instanceof KeyboardEvent && event.key === ' ') {
        event.preventDefault();
      }
      this.expandSidebar();
    }
  }

  openProfileDialog(): void {
    this.profileDialogVisible.set(true);
  }

  onLogin(): void {
    this.login.emit();
  }

  onLogout(): void {
    this.profileDialogVisible.set(false);
    this.logout.emit();
  }
}
